/*
 * drawer-ux.js — imported-header drawer behaviour (mobile menu + cart) for sites
 * built from HTML import (e.g. Babylone Outdoor). The header markup uses
 *   <button data-open="mobile">  →  #drawerMobile
 *   <button data-open="cart">    →  #drawerCart
 *   [data-close] / .scrim        →  close
 *
 * SELF-HEAL: the seed's header rebuild can drop the imported #drawerMobile panel
 * (the burger button survives but its target doesn't), leaving the mobile menu
 * dead. When the burger is tapped and #drawerMobile is missing, this builds one on
 * the fly from the page's category nav + header action links (and a scrim), fully
 * self-styled — so the mobile menu works on every page without touching stored
 * content.
 *
 * No-op on sites without these elements (event-delegated; guarded ids).
 */
(function () {
  // Own guard flag. Some sites carry an OLD inline copy of this logic in their
  // page/layout CustomJs (marker /*BABYLONE-UX*/) that only knows
  // data-open="menu|cart" and claims window.__babyloneUx FIRST now that
  // CustomJs is static-emitted — keying our guard on it disabled this script
  // and killed data-open="mobile" burgers (Babylone prod, 2026-07-21).
  // We still SET __babyloneUx so the legacy copy stands down when we run first;
  // when it ran first, both run — the class toggles are idempotent.
  if (window.__pdDrawerUx) return;
  window.__pdDrawerUx = true;
  window.__babyloneUx = true;

  function injectFallbackCss() {
    if (document.getElementById('cbf-drawer-css')) return;
    var s = document.createElement('style');
    s.id = 'cbf-drawer-css';
    s.textContent =
      '#drawerMobile.cbf-built{position:fixed;top:0;left:0;height:100%;width:min(82vw,340px);background:#fff;z-index:2147483300;transform:translateX(-100%);transition:transform .25s ease;display:flex;flex-direction:column;box-shadow:2px 0 24px rgba(0,0,0,.25);overflow-y:auto}' +
      '#drawerMobile.cbf-built.open{transform:none}' +
      '#drawerMobile.cbf-built .drawer__head{display:flex;align-items:center;justify-content:space-between;padding:15px 16px;border-bottom:1px solid #eee;font-weight:700;font-family:system-ui,sans-serif;color:#0E2236}' +
      '#drawerMobile.cbf-built .close-btn{background:none;border:none;font-size:26px;line-height:1;cursor:pointer;color:#333;padding:0 4px}' +
      '#drawerMobile.cbf-built nav{display:flex;flex-direction:column;padding:6px 0}' +
      '#drawerMobile.cbf-built nav a{padding:12px 18px;color:#0E2236;text-decoration:none;border-bottom:1px solid #f4f4f4;font:500 15px/1.3 system-ui,sans-serif}' +
      '#drawerMobile.cbf-built nav a:hover{background:#f7f7f7}' +
      // Dark variant — matches dark-themed sites (e.g. Palladium Cookies): dark
      // panel, light text, brand accent on hover (--green-accent falls back to teal).
      '#drawerMobile.cbf-built.cbf-dark{background:#0b1420;color:#e8ecf4}' +
      '#drawerMobile.cbf-built.cbf-dark .drawer__head{border-bottom-color:rgba(255,255,255,.10);color:#fff}' +
      '#drawerMobile.cbf-built.cbf-dark .close-btn{color:#cbd5e1}' +
      '#drawerMobile.cbf-built.cbf-dark nav a{color:#e8ecf4;border-bottom-color:rgba(255,255,255,.07)}' +
      '#drawerMobile.cbf-built.cbf-dark nav a:hover{background:rgba(255,255,255,.06);color:var(--green-accent,#5EEAD4)}' +
      '.cbf-scrim{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:2147483290;opacity:0;visibility:hidden;transition:opacity .25s}' +
      '.cbf-scrim.open{opacity:1;visibility:visible}';
    document.head.appendChild(s);
  }

  // True when the page uses a dark theme — so the built drawer matches it
  // instead of always being white. Reads the body/html background luminance.
  function isDarkTheme() {
    try {
      function lum(el) {
        var c = getComputedStyle(el).backgroundColor || '';
        var m = c.match(/[\d.]+/g);
        if (!m || m.length < 3) return null;
        var a = m.length >= 4 ? parseFloat(m[3]) : 1;
        if (a === 0) return null; // transparent — try the next element up
        return (0.2126 * +m[0] + 0.7152 * +m[1] + 0.0722 * +m[2]) / 255;
      }
      var l = lum(document.body);
      if (l == null) l = lum(document.documentElement);
      return l != null && l < 0.4;
    } catch (e) { return false; }
  }

  function esc(t) {
    return String(t == null ? '' : t).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function buildMobileDrawer() {
    injectFallbackCss();
    var d = document.createElement('aside');
    d.id = 'drawerMobile';
    d.className = 'drawer drawer--left cbf-built' + (isDarkTheme() ? ' cbf-dark' : '');
    d.setAttribute('aria-hidden', 'true');
    d.setAttribute('aria-label', 'Menu');

    var html = '<div class="drawer__head"><span>Menu</span>'
      + '<button class="close-btn" type="button" aria-label="Fermer" data-close>&times;</button></div><nav>';
    var seen = {};
    function add(href, txt) {
      if (!href || !txt) return;
      // Skip in-page anchors whose target isn't on this page (e.g. landing
      // #sections listed in the header nav but absent on /scan) — dead entries.
      if (href.charAt(0) === '#' && href.length > 1 && !document.querySelector(href)) return;
      var k = href + '|' + txt;
      if (seen[k]) return; seen[k] = 1;
      html += '<a href="' + esc(href) + '">' + esc(txt) + '</a>';
    }
    // Primary nav links: Babylone commerce nav + imported landing/cookies header nav.
    document.querySelectorAll('.commerce-nav-link, .header__nav a[href]').forEach(function (a) {
      add(a.getAttribute('href'), (a.textContent || '').trim());
    });
    // Header action links (login/register, Favoris / Compte / Panier) — labelled by aria-label or text.
    document.querySelectorAll('.header__actions a[href], .commerce-header a[href]').forEach(function (a) {
      add(a.getAttribute('href'), (a.getAttribute('aria-label') || a.textContent || '').trim());
    });
    html += '</nav>';
    d.innerHTML = html;
    document.body.appendChild(d);
    return d;
  }

  function ensureScrim() {
    var s = document.querySelector('.scrim, .cbf-scrim');
    if (!s) {
      injectFallbackCss();
      s = document.createElement('div');
      s.className = 'scrim cbf-scrim';
      document.body.appendChild(s);
    }
    return s;
  }

  // Verrou de défilement : overflow:hidden ne suffit pas sur iOS — la page défilait derrière le tiroir (recette Léa
  // 21/10/2026). Le corps est figé à sa position, rendue à la fermeture ; le voile bloque le geste tactile.
  var lockedY = null;
  function lockScroll() {
    if (lockedY !== null) return;
    lockedY = window.scrollY || 0;
    var st = document.getElementById('cbf-lock-css');
    if (!st) {
      st = document.createElement('style'); st.id = 'cbf-lock-css';
      st.textContent = 'html.cbf-lock{overflow:hidden!important;overscroll-behavior:none}' +
        'html.cbf-lock body{position:fixed!important;left:0;right:0;width:100%;overflow:hidden!important}' +
        '.drawer.open{overscroll-behavior:contain;-webkit-overflow-scrolling:touch}' +
        'html.cbf-lock .pd-backtop{visibility:hidden!important}';
      document.head.appendChild(st);
    }
    document.body.style.top = (-lockedY) + 'px';
    document.documentElement.classList.add('cbf-lock');
  }
  function unlockScroll() {
    if (lockedY === null) return;
    document.documentElement.classList.remove('cbf-lock');
    document.body.style.removeProperty('top');
    var y = lockedY; lockedY = null;
    window.scrollTo(0, y);
  }
  document.addEventListener('touchmove', function (e) {
    if (lockedY === null) return;
    var inDrawer = e.target && e.target.closest && e.target.closest('.drawer.open');
    if (!inDrawer) e.preventDefault();
  }, { passive: false });

  function closeAll() {
    unlockScroll();
    document.querySelectorAll('.drawer.open').forEach(function (d) {
      d.classList.remove('open');
      d.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('.scrim.open, .cbf-scrim.open').forEach(function (s) {
      s.classList.remove('open');
      if (s.getAttribute('data-cbf-shown')) { s.style.removeProperty('display'); s.removeAttribute('data-cbf-shown'); }
    });
  }

  // Le voile importé peut rester display:none même avec la classe « open » (feuille du site) : rien à
  // toucher à côté du tiroir, le clic atteint la page et le menu reste ouvert (Babylone, 14/10/2026).
  // Voile forcé visible à l'ouverture, remis tel quel à la fermeture.
  function showScrim(s) {
    s.classList.add('open');
    try {
      if (getComputedStyle(s).display === 'none') { s.style.setProperty('display', 'block', 'important'); s.setAttribute('data-cbf-shown', '1'); }
    } catch (err) { }
  }

  // ── Sous-menus du tiroir mobile : les collections d'une famille, reprises du méga-menu de l'en-tête ──
  // Sur le bureau, « Pergola » déroule ses collections (Fixation murale, Pose libre) ; le tiroir importé
  // n'avait que des liens plats. Pour chaque entrée du tiroir dont l'adresse est celle d'un lien du menu
  // principal, les liens de son panneau (.commerce-nav-mega-links a, hors « Toutes les collections »)
  // forment une sous-liste repliée derrière un bouton. Une famille sans collection (Carport) reste un lien
  // simple : rien à lister (Babylone, 14/10/2026). Idempotent ; rejoué à l'ouverture (nœuds re-rendus).
  function injectSubmenuCss() {
    if (document.getElementById('cbf-sub-css')) return;
    var s = document.createElement('style');
    s.id = 'cbf-sub-css';
    s.textContent =
      '.mmenu__item{display:flex;flex-wrap:wrap;align-items:center}' +
      '.mmenu__item>a{flex:1 1 auto;min-width:0}' +
      '.mmenu__more{flex:0 0 auto;background:none;border:0;padding:10px 14px;margin:0;cursor:pointer;color:inherit;line-height:0;border-radius:8px}' +
      '.mmenu__more:hover{background:rgba(0,0,0,.05)}' +
      '.mmenu__more svg{width:18px;height:18px;transition:transform .2s}' +
      '.mmenu__item.is-open .mmenu__more svg{transform:rotate(180deg)}' +
      '.mmenu__sub{display:none;flex:1 1 100%;padding:0 0 10px 22px}' +
      '.mmenu__item.is-open .mmenu__sub{display:block}' +
      '.mmenu__sub a{display:block;padding:9px 0;font-size:.95em;color:inherit;text-decoration:none;opacity:.88;border-bottom:1px solid rgba(0,0,0,.06)}' +
      '.mmenu__sub a:last-child{border-bottom:0}' +
      '.mmenu__sub a.mmenu__sub-all{font-weight:600;opacity:1}' +
      // Ligne à sous-menu : le chevron importé du lien (::after « › ») doublait la flèche de dépliage (recette Léa 21/10/2026).
      '.mmenu__item>.mmenu__link::after,.mmenu__item>a::after{content:none!important;display:none!important}' +
      '.mmenu__item>a>svg,.mmenu__item>a>.mmenu__arrow,.mmenu__item>a>.chevron{display:none!important}';
    document.head.appendChild(s);
  }
  function samePath(a, b) {
    try { return new URL(a, location.href).pathname + new URL(a, location.href).search === new URL(b, location.href).pathname + new URL(b, location.href).search; } catch (err) { return a === b; }
  }
  function addDrawerSubmenus() {
    var drawer = document.getElementById('drawerMobile');
    if (!drawer) return;
    var items = document.querySelectorAll('#bab-header-nav .commerce-nav-item, .commerce-nav-item');
    if (!items.length) return;
    injectSubmenuCss();
    drawer.querySelectorAll('nav a[href]').forEach(function (link) {
      if (link.closest('.mmenu__sub') || link.parentElement.classList.contains('mmenu__item')) return;
      var href = link.getAttribute('href');
      var subs = null, allLink = null;
      items.forEach(function (li) {
        if (subs) return;
        var top = li.querySelector(':scope > a[href]');
        if (!top || !samePath(top.getAttribute('href'), href)) return;
        var found = li.querySelectorAll('.commerce-nav-mega-links a[href]:not(.commerce-nav-mega-all)');
        if (found.length) { subs = found; allLink = li.querySelector('.commerce-nav-mega-all'); }
      });
      if (!subs) return;
      var wrap = document.createElement('div');
      wrap.className = 'mmenu__item';
      link.parentNode.insertBefore(wrap, link);
      wrap.appendChild(link);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mmenu__more';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-label', 'Voir les collections : ' + (link.textContent || '').trim());
      btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
      btn.addEventListener('click', function (ev) {
        ev.preventDefault(); ev.stopPropagation();
        var open = wrap.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      wrap.appendChild(btn);
      var sub = document.createElement('div');
      sub.className = 'mmenu__sub';
      var html = '';
      subs.forEach(function (a) { html += '<a href="' + esc(a.getAttribute('href')) + '">' + esc((a.textContent || '').trim()) + '</a>'; });
      if (allLink) html += '<a class="mmenu__sub-all" href="' + esc(allLink.getAttribute('href')) + '">' + esc((allLink.textContent || '').trim()) + '</a>';
      sub.innerHTML = html;
      wrap.appendChild(sub);
      // La famille courante arrive dépliée.
      if (samePath(href, location.pathname + location.search)) { wrap.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', addDrawerSubmenus);
  else addDrawerSubmenus();

  document.addEventListener('click', function (e) {
    // Clic hors d'un tiroir ouvert (page assombrie, voile ou pas) : il se ferme. L'ouvreur garde son rôle.
    var openDrawer = document.querySelector('.drawer.open');
    if (openDrawer && !openDrawer.contains(e.target) && !(e.target.closest && e.target.closest('[data-open], .header__menu-btn'))) {
      closeAll();
      return;
    }
    // Openers: explicit [data-open] (Babylone drawers) OR the imported
    // landing/cookies header burger (.header__menu-btn), which carries no
    // data-open and has no drawer target — treat it as the mobile menu.
    var o = e.target.closest && e.target.closest('[data-open], .header__menu-btn');
    if (o) {
      var w = o.getAttribute('data-open') || 'mobile';
      var el = (w === 'mobile' || w === 'menu')
                 ? (document.getElementById('drawerMobile') || buildMobileDrawer())
             : w === 'cart' ? document.getElementById('drawerCart')
             : null;
      if (el) {
        e.preventDefault();
        try { addDrawerSubmenus(); } catch (err) { }
        el.classList.add('open');
        el.setAttribute('aria-hidden', 'false');
        showScrim(ensureScrim());
        lockScroll();
      }
      return;
    }
    // Close: explicit [data-close], the × button (.close-btn / aria-label="Fermer"),
    // or the scrim backdrop.
    if ((e.target.closest && e.target.closest('[data-close], .close-btn, [aria-label="Fermer"]')) ||
        (e.target.classList && (e.target.classList.contains('scrim') || e.target.classList.contains('cbf-scrim')))) {
      e.preventDefault();
      closeAll();
      return;
    }
    // Tapping a nav entry inside an open drawer should close it too.
    if (e.target.closest && e.target.closest('.drawer.open a[href], .drawer.open [role="link"], .drawer.open [href]')) {
      closeAll();
    }
  });

  // ESC closes any open drawer.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') closeAll();
  });

  // Footer copyright year — rendered after the Blazor circuit connects; retry briefly.
  function setYear() {
    var y = document.getElementById('year');
    if (y) { y.textContent = new Date().getFullYear(); return true; }
    return false;
  }
  if (!setYear()) {
    var tries = 0;
    var iv = setInterval(function () { if (setYear() || ++tries > 40) clearInterval(iv); }, 150);
  }

  // ── Visiteur non connecté : bouton « Se connecter » dans l'en-tête ──
  // Sur les en-têtes importés type Babylone (#bab-header-actions), un visiteur
  // anonyme voit un bouton de connexion à la place de Favoris + Compte (qui
  // n'ont de sens que connecté). Le Panier RESTE visible : l'achat anonyme est
  // supporté (panier cookie 90 j, fusion à la connexion). L'état vient de la
  // classe body pd-anon/pd-auth rendue côté serveur (App.razor) — fiable même
  // avec le cache de sortie (anonyme uniquement). No-op sur les autres sites.
  function anonLoginButton() {
    // Connecté : jamais de « Se connecter » à côté de Favoris + Compte (recette Léa 21/10/2026, capture : les trois à la
    // fois, bouton sans style — la feuille injectée avait disparu après une navigation). On retire un bouton resté.
    var stale = document.getElementById('bab-login-btn');
    if (!document.body.classList.contains('pd-anon')) { if (stale) stale.parentNode.removeChild(stale); return; }
    var actions = document.getElementById('bab-header-actions');
    if (!actions) return;

    if (!document.getElementById('cbf-login-css')) {
      var s = document.createElement('style');
      s.id = 'cbf-login-css';
      s.textContent =
        // !important : html-import-interactions réécrit les styles inline des
        // nœuds importés après nous — une règle CSS !important reste maîtresse.
        'body.pd-anon #bab-header-wishlist,body.pd-anon #bab-header-compte{display:none!important}' +
        'body:not(.pd-anon) #bab-login-btn{display:none!important}' +
        '#bab-login-btn{display:inline-flex;align-items:center;gap:8px;padding:9px 16px;margin-right:6px;' +
        'background:rgba(255,255,255,.14);border:1px solid rgba(255,255,255,.35);border-radius:999px;' +
        'color:#fff;font-weight:600;font-size:14px;line-height:1.2;font-family:inherit;text-decoration:none;white-space:nowrap;cursor:pointer}' +
        '#bab-login-btn:hover{background:rgba(255,255,255,.24)}' +
        '#bab-login-btn svg{flex:0 0 auto}';
      document.head.appendChild(s);
    }
    if (stale) return;

    var a = document.createElement('a');
    a.id = 'bab-login-btn';
    a.href = '/Login?returnUrl=%2Faccount';
    a.setAttribute('aria-label', 'Se connecter');
    a.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<circle cx="12" cy="8" r="5"></circle><path d="M20 21a8 8 0 0 0-16 0"></path></svg>' +
      '<span>Se connecter</span>';
    var cart = document.getElementById('bab-header-cart');
    actions.insertBefore(a, cart || null);
  }
  // ── Visiteur connecté : son prénom dans le bouton « Compte » ──
  // Le serveur pose data-pd-user="Prénom" sur le body (App.razor, jamais sur une
  // page anonyme). Cibles : le libellé du bouton Compte des en-têtes importés
  // (#bab-header-compte .icon-btn__label) et tout [data-pd-user-label] d'un autre
  // thème. Rejoué après le bootstrap des nœuds importés, qui peut réécrire le DOM.
  function signedInName() {
    var name = document.body.getAttribute('data-pd-user');
    if (!name || !document.body.classList.contains('pd-auth')) return;
    var nodes = document.querySelectorAll('#bab-header-compte .icon-btn__label, [data-pd-user-label]');
    for (var i = 0; i < nodes.length; i++) if (nodes[i].textContent !== name) nodes[i].textContent = name;
    var btn = document.querySelector('#bab-header-compte .icon-btn');
    if (btn) btn.setAttribute('aria-label', 'Mon compte \u2014 ' + name);
  }
  function headerState() { anonLoginButton(); signedInName(); setTimeout(signedInName, 600); setTimeout(signedInName, 2000); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', headerState);
  else headerState();
  // Page rendue depuis le cache du navigateur (retour arrière) ou navigation améliorée de Blazor : l'en-tête est rejoué
  // (feuille et bouton recréés ou retirés selon la classe pd-anon / pd-auth du body).
  window.addEventListener('pageshow', function (e) { if (e.persisted) headerState(); });
  document.addEventListener('enhancedload', headerState);
  try { if (window.Blazor && window.Blazor.addEventListener) window.Blazor.addEventListener('enhancedload', headerState); } catch (x) { }
})();

/* Photos produit protégées (réglage boutique, classe commerce-photos-protected sur la galerie et le zoom) : ni menu
   « Enregistrer l'image » ni glisser-déposer. Écouteurs délégués : la galerie est rendue et re-rendue par Blazor. */
(function () {
  if (window.__pdPhotoGuard) return;
  window.__pdPhotoGuard = true;
  var guarded = function (e) {
    var t = e.target;
    return t && t.tagName === 'IMG' && t.closest && t.closest('.commerce-photos-protected');
  };
  document.addEventListener('contextmenu', function (e) { if (guarded(e)) e.preventDefault(); }, true);
  document.addEventListener('dragstart', function (e) { if (guarded(e)) e.preventDefault(); }, true);
})();

/* En-tête collant + `scroll-padding-top` (ancres sous l'en-tête) : prendre le focus dans l'en-tête — clic ou Tab dans la
   barre de recherche — faisait remonter la page en haut : le navigateur « ramène » l'élément sous la zone de
   scroll-padding alors qu'il est déjà visible. On restaure la position d'avant le focus (retour Babylone 20/10/2026). */
(function () {
    var lastY = 0, lastT = 0;
    var inSticky = function (el) {
        for (var n = el; n && n !== document.body; n = n.parentElement) {
            var pos = getComputedStyle(n).position;
            if (pos === 'sticky' || pos === 'fixed') return true;
        }
        return false;
    };
    var remember = function () { lastY = window.scrollY; lastT = Date.now(); };
    document.addEventListener('pointerdown', remember, true);
    document.addEventListener('keydown', remember, true);
    document.addEventListener('focusin', function (e) {
        var t = e.target;
        if (!t || !t.matches || !t.matches('input, textarea, select, button, a, [tabindex]') || !inSticky(t)) return;
        var y = (Date.now() - lastT < 1500) ? lastY : window.scrollY;
        var html = document.documentElement;
        var restore = function () {
            if (Math.abs(window.scrollY - y) < 2) return;
            var prev = html.style.scrollBehavior;
            html.style.scrollBehavior = 'auto';
            window.scrollTo(0, y);
            html.style.scrollBehavior = prev;
        };
        restore(); requestAnimationFrame(restore); setTimeout(restore, 60); setTimeout(restore, 200); setTimeout(restore, 450);
    }, true);
})();

/* « Gérer les cookies » (pied de page) : rouvre les préférences du bandeau — Palladium Cookies (`window.Palladium.open()`) ou
   le bandeau interne (`PdCookieConsent.open()`). CNIL : retirer son consentement doit être aussi simple que le donner
   (recette Léa 20/10/2026). Lien : href="#gerer-cookies" ou attribut data-cookie-prefs. */
(function () {
    document.addEventListener('click', function (e) {
        var a = e.target && e.target.closest ? e.target.closest('a[href="#gerer-cookies"], [data-cookie-prefs]') : null;
        if (!a) return;
        var api = (window.Palladium && typeof window.Palladium.open === 'function') ? window.Palladium
            : (window.PdCookieConsent && typeof window.PdCookieConsent.open === 'function') ? window.PdCookieConsent : null;
        if (!api) return;
        e.preventDefault();
        api.open();
    }, true);
})();

/* Blocs importés cliquables : un conteneur portant data-href (tuiles d'aide de la page Support, diapo 25 du retour Babylone)
   se comporte comme un lien — clic, Entrée ou Espace ; un vrai lien à l'intérieur garde la main. */
(function () {
    function go(el) { var href = el.getAttribute('data-href'); if (href) window.location.href = href; }
    document.addEventListener('click', function (e) {
        var t = e.target && e.target.closest ? e.target.closest('[data-href]') : null;
        if (!t || (e.target.closest && e.target.closest('a[href], button'))) return;
        go(t);
    });
    document.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var t = e.target && e.target.matches && e.target.matches('[data-href]') ? e.target : null;
        if (!t) return;
        e.preventDefault(); go(t);
    });
})();
